import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { DownloadService } from '../../../core/services/download.service';
import { FileCheck } from '../../../core/models/file-check.model';
import { FileChecksService } from '../../../core/services/file-checks.service';
import { ClientFileKind } from '../../../core/enums/client-file-kind.enum';
import { LimitOffsetPaging } from 'app/core/models/paged-results/limit-offset-paging.model';
import { ClientsService } from '../../../core/services/clients.service';
import { FileCheckFilters } from 'app/core/models/user-settings/file-check-settings.model';
import { FormGroup, FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FileCheckNavComponent } from '../../components/file-check-nav/file-check-nav.component';
import { NgSelectComponent } from '@ng-select/ng-select';
import { DecimalPipe, DatePipe } from '@angular/common';
import { PaginationHeaderComponent } from '../../../shared/components/pagination-header/pagination-header.component';
import { FileCheckStatusComponent } from '../../components/file-check-status/file-check-status.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbDropdown, NgbDropdownToggle, NgbDropdownMenu } from '@ng-bootstrap/ng-bootstrap';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-capture-admin-file-check-results',
    templateUrl: './capture-admin-file-check-results.component.html',
    styleUrls: ['./capture-admin-file-check-results.component.scss'],
    imports: [FileCheckNavComponent,
              FormsModule,
              ReactiveFormsModule,
              NgSelectComponent,
              PaginationHeaderComponent,
              FileCheckStatusComponent,
              FaIconComponent,
              NgbDropdown,
              NgbDropdownToggle,
              NgbDropdownMenu,
              PaginationComponent,
              MatProgressSpinner,
              DecimalPipe,
              DatePipe]
})
export class CaptureAdminFileCheckResultsComponent implements OnInit, OnDestroy {
  private fileChecksService = inject(FileChecksService);
  private downloadService = inject(DownloadService);
  private clientsService = inject(ClientsService);
  private formBuilder = inject(FormBuilder);

  loading = false;
  filters: FileCheckFilters;
  fileChecks: FileCheck[] = [];
  nextPollTimeout = null;
  clients;

  fileTypeToDisplayName = {
    [ClientFileKind.referral]: 'Referral',
    [ClientFileKind.encounter]: 'Encounter',
    [ClientFileKind.clientPrescription]: 'Client Prescription',
  };

  paging: LimitOffsetPaging = LimitOffsetPaging.empty;

  formGroup: FormGroup;

  ngOnInit() {
    this.initFormGroup();
    this.loadClients();
    this.loadFileChecks(true);
  }

  ngOnDestroy() {
    clearTimeout(this.nextPollTimeout);
  }

  onCancelClick($event, fileCheck: FileCheck) {
    $event.preventDefault();
    this.fileChecksService.cancel(fileCheck.id).subscribe(canceledFileCheck => {
      const index = this.fileChecks.findIndex(fc => fc.id === canceledFileCheck.id);

      if (index > -1) {
        this.fileChecks[index] = canceledFileCheck;
        this.fileChecks = [...this.fileChecks];
      }
    });
  }

  onPageSizeChange(pageSize) {
    this.paging.pageSize = pageSize;
    this.loadFileChecks();
  }

  onPageChange() {
    this.loadFileChecks();
  }

  onDownloadOriginalFileClick($event, fileCheck: FileCheck) {
    $event.preventDefault();
    this.fileChecksService.get(fileCheck.id).subscribe(fc => {
      this.downloadService.getDownloadUrl(fc.fileExpirableDownloadPath).subscribe(expirableDownload => {
        this.downloadService.download(expirableDownload.url);
      });
    });
  }

  onDownloadErrorFileClick($event, fileCheck: FileCheck) {
    $event.preventDefault();
    this.fileChecksService.get(fileCheck.id).subscribe(fc => {
      this.downloadService.getDownloadUrl(fc.errorFileExpirableDownloadPath).subscribe(expirableDownload => {
        this.downloadService.download(expirableDownload.url);
      });
    });
  }

  onResetFiltersClick() {
    this.paging.currentPage = 1;

    this.filters = new FileCheckFilters();

    this.loadFileChecks();
    this.initFormGroup();
  }

  private loadFileChecks(loading = true) {
    const paging = { page: this.paging.currentPage, pageSize: this.paging.pageSize };

    this.loading = loading;

    this.fileChecksService.getList(paging, this.filters).subscribe(data => {
      this.fileChecks = data.records;
      this.paging = data.meta.paging;
      this.loading = false;

      const anyActive: boolean = this.fileChecks.some(fc => fc.active);

      if (anyActive) {
        this.nextPollTimeout = setTimeout(() => this.loadFileChecks(false), 5000);
      }
    });
  }

  private loadClients() {
    this.clientsService.getList(null, { applicableToReferralClaims: true }).subscribe(result => {
      this.clients = result.clients;
    });
  }

  private initFormGroup() {
    this.formGroup = this.formBuilder.group({
      filters: this.formBuilder.group({
        clientIds: []
      })
    });

    this.formGroup.valueChanges.subscribe(_value => {
      this.paging.currentPage = 1;

      const formValue = this.formGroup.value;

      this.filters = new FileCheckFilters();
      this.filters.clientIds = formValue.filters.clientIds;

      this.loadFileChecks();
    })
  }
}
